<template>
  <section class="adventure-search">
    <div class="mx-auto max-w-368 mt-32 mb-64 px-16 lg:px-0 md:max-w-386 lg:max-w-700 xl:max-w-1072 lg:mt-48">
      <h1 class="text-h2 pb-8">Adventures</h1>
      <p v-if="adventuresList.length || isLoading" class="text-body pb-8">
        One-of-a-kind at-home experiences designed especially for kids
      </p>
      <div class="flex w-[calc(100%+16px)] sm:w-full overflow-scroll hide-scrollbar sm:overflow-visible">
        <FilterToken :buttonLabel="zipcodeLabel" modalLabel="Location" :selected="!!zipcode">
          <template #default="{ closeModal }">
            <ZipCode :closeModal="closeModal" :zipcode="zipcode" :distance="distance" />
          </template>
        </FilterToken>
        <FilterToken :buttonLabel="ageLabel" modalLabel="Age" class="ml-8" :selected="filterAges.length > 0">
          <template #default="{ closeModal }">
            <AgePopup :closeModal="closeModal" :ageRanges="ageRanges" :filterAges="filterAges" />
          </template>
        </FilterToken>
        <FilterToken :buttonLabel="categoryLabel" modalLabel="Category" class="ml-8" :selected="filterCategories.length > 0">
          <template #default="{ closeModal }">
            <CategoryPopup :closeModal="closeModal" :allCategories="allCategories" :filterCategories="filterCategories" />
          </template>
        </FilterToken>
      </div>
      <div v-if="isLoading">
        <SearchSkeleton />
      </div>
      <div v-else>
        <div v-if="adventuresList.length">
          <div class="w-full flex flex-col lg:grid lg:grid-cols-2 xl:grid-cols-3 gap-16">
            <ComplexCard
              v-for="adventure in adventuresList"
              :id="`adventure-${adventure.id}`"
              :key="adventure.id"
              variant="base"
              :headerImageUrl="adventure.adventureCategoryImage && adventure.adventureCategoryImage.url"
              :title="adventure.title"
              :firstLineText="firstLineText(adventure.sitterFirstName, adventure.sitterLastInitial)"
              :secondLineText="secondLineText(adventure.ageRangeText, adventure.adventureCategoryName)"
              :avatarFirstName="adventure.sitterFirstName"
              :avatarLastInitial="adventure.sitterLastInitial"
              :avatarImageUrl="adventure.sitterImageUrl"
              :minPayRate="adventure.minPayRate"
              :maxPayRate="adventure.maxPayRate"
              :detailsUrl="adventureDetailsUrl(adventure.sitterLocation, adventure.id)"
            />
          </div>
          <div v-if="hasNextPage" class="w-full md:w-340 lg:w-max mx-auto mt-32">
            <Button
              id="showMore"
              class="w-full lg:w-auto"
              variant="outlined"
              value="Show More"
              size="small"
              :loading="isLoadingMore"
              @click="runSearch(lastAdventure)"
            />
          </div>
        </div>
        <Card v-else class="w-full px-16 md:max-w-448 lg:max-w-768">
          <h2 class="text-h3 mb-4">No results found</h2>
          <p class="text-body">The Adventures you’re looking for are still being created—try updating your search filters.</p>
        </Card>
      </div>
    </div>

    <div v-if="adventuresList.length" class="bg-grey-background-contrast flex flex-col items-center p-16 lg:py-32">
      <h2 class="text-h4 text-center mb-24">Book an Adventure today</h2>
      <div class="flex flex-col gap-24 max-w-368 lg:flex-row lg:max-w-768">
        <div>
          <SitterTeamIllustration :width="64" :height="64" class="mx-auto mb-8" aria-hidden="true" />
          <p class="text-body text-center">Lighten your load with thoughtfully planned activities</p>
        </div>
        <div>
          <CalendarEmptyIllustration :width="64" :height="64" class="mx-auto mb-8" aria-hidden="true" />
          <p class="text-body text-center">Test out new interests without a long-term commitment</p>
        </div>
        <div>
          <CognitiveLoadIllustration :width="64" :height="64" class="mx-auto mb-8" aria-hidden="true" />
          <p class="text-body text-center">Make the most of a babysitter by adding enrichment</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { v4 as uuidv4 } from 'uuid';

import SearchSkeleton from './SearchSkeleton.vue';

import AgePopup from '@/components/filter_token/filters/AgePopup.vue';
import Button from '@/components/button/Button.vue';
import CategoryPopup from '@/components/category_popup/CategoryPopup.vue';
import Card from '@/components/card/Card.vue';
import ComplexCard from '@/components/complex_card/ComplexCard.vue';
import FilterToken from '@/components/filter_token/FilterToken.vue';
import ZipCode from '@/components/filter_token/filters/ZipCode.vue';

import SitterTeamIllustration from '@/graphics/illustrations/SitterTeamIllustration.vue';
import CalendarEmptyIllustration from '@/graphics/illustrations/CalendarEmptyIllustration.vue';
import CognitiveLoadIllustration from '@/graphics/illustrations/CognitiveLoadIllustration.vue';

import { readDomainCookie, writeDomainCookie } from '@/lib/domain_cookie';

import { ADVENTURE_SEARCH_QUERY, ADVENTURE_METADATA_QUERY } from '@/graphql/adventures/queries';

import adventures from '@/lib/adventures';
import { geocode, isValidZipcode } from '@/lib/geocode';

export default {
  name: 'Search',
  components: {
    SearchSkeleton,

    AgePopup,
    Button,
    CategoryPopup,
    Card,
    ComplexCard,
    FilterToken,
    ZipCode,

    SitterTeamIllustration,
    CalendarEmptyIllustration,
    CognitiveLoadIllustration,
  },
  data(): any {
    return {
      adventureEdges: [],
      hasNextPage: false,
      isLoading: true,
      isLoadingMore: false,
    };
  },
  computed: {
    adventuresList(): any {
      return this.adventureEdges.map(({ node }) => node?.adventure);
    },
    lastAdventure(): string {
      if (this.hasNextPage) {
        return this.adventureEdges[this.adventureEdges.length - 1].cursor;
      }
      return '';
    },
    zipcode(): string {
      return this.$route.query.zipcode;
    },
    distance(): number {
      return this.$route.query.distance || '15';
    },
    zipcodeLabel(): string {
      if (!this.zipcode) {
        return 'Location';
      }
      return this.distance && `${this.distance} mi`;
    },
    filterCategories(): Array<any> {
      return this.$route.query.adventureCategoryName ? JSON.parse(this.$route.query.adventureCategoryName) : [];
    },
    categoryLabel(): string {
      if (this.filterCategories.length === 1) {
        return this.filterCategories[0];
      }
      if (this.filterCategories.length === 0) {
        return 'Category';
      }
      const multipleLabel = `(${this.filterCategories.length}) Category`;
      return multipleLabel;
    },
    ageRanges(): string[] {
      return this.adventureMetaData?.categories?.[0].ageRanges || [];
    },
    filterAges(): Array<any> {
      return this.$route.query.ageRangeId ? JSON.parse(this.$route.query.ageRangeId).sort() : [];
    },
    ageLabel(): string {
      const filterLength = this.filterAges.length;

      const minRange = this.ageRanges.filter((ageRange) => ageRange.id === this.filterAges[0])[0]?.minRange;
      const maxRange = this.ageRanges.filter((ageRange) => ageRange.id === this.filterAges[filterLength - 1])[0]?.maxRange;

      if (filterLength > 0 && !(filterLength === 2 && minRange === 3 && maxRange === null)) {
        if (minRange !== 11) {
          return `Age ${minRange} - ${maxRange || '11+'}`;
        }
        return 'Age 11+';
      }
      return 'Age';
    },
    account() {
      return this.$store.getters['account/getAccount'];
    },
  },
  watch: {
    $route: async function () {
      await this.runSearch();
    },
  },
  async created() {
    if (this.$route.query.zipcode) {
      await this.runSearch();
    } else {
      this.setDefaultQueryParams();
    }
  },
  methods: {
    async setDefaultQueryParams() {
      const zipcode = readDomainCookie('adventureSearchZip') || this.account?.location?.zipcode || '10001';
      const distance = readDomainCookie('adventureSearchDistance') || '15';
      const locationData = {
        zipcode,
        distance,
        sid: uuidv4(),
        careType: 'adventures',
      };
      this.$router?.push({ query: locationData });
    },
    setAdventureSearchCookies(): void {
      if (this.zipcode) {
        writeDomainCookie('adventureSearchZip', this.zipcode);
      }
      if (this.distance) {
        writeDomainCookie('adventureSearchDistance', this.distance);
      }
    },
    async getSearchParams() {
      const geoPoint = await this.getSearchGeoPoint();
      const params = this.buildSearchParams(geoPoint);
      return params;
    },
    async getSearchGeoPoint() {
      if (isValidZipcode(this.zipcode)) {
        return this.getGeoPointForZipcode(this.account);
      }
      return this.getAccountGeoPoint(this.account);
    },
    async getGeoPointForZipcode(account) {
      if (this.isZipcodeSameAsAccount(account)) {
        return this.getAccountGeoPoint(account);
      }
      return this.getGeocodedGeoPoint(account);
    },
    isZipcodeSameAsAccount(account) {
      return this.zipcode === account?.location?.zipcode;
    },
    getAccountGeoPoint(account) {
      return {
        x: account?.location?.longitude || -87.63,
        y: account?.location?.latitude || 41.89,
      };
    },
    async getGeocodedGeoPoint(account) {
      try {
        const geocoded = await geocode(this.zipcode);
        if (geocoded?.location) {
          return {
            x: geocoded.location.x,
            y: geocoded.location.y,
          };
        }
        return this.getAccountGeoPoint(account);
      } catch (error) {
        console.error('Geocoding failed:', error);
        return this.getAccountGeoPoint(account);
      }
    },
    buildSearchParams(geoPoint) {
      const params: any = { geoPoint, distance: Number(this.distance) };

      if (this.filterCategories) {
        params.adventureCategoryName = this.filterCategories;
      }
      if (this.filterAges) {
        params.ageRangeId = this.filterAges;
      }

      return params;
    },
    async runSearch(after = '') {
      if (!after) {
        this.adventureEdges = [];
        this.isLoading = true;
      } else {
        this.isLoadingMore = true;
      }

      try {
        const searchParams = await this.getSearchParams();
        const adventureSearchQuery = {
          after,
          first: 15,
          searchParams,
        };

        const { data } = await this.$apollo.query({
          query: ADVENTURE_SEARCH_QUERY,
          variables: adventureSearchQuery,
          fetchPolicy: 'network-only',
        });

        const { edges, pageInfo } = data?.adventureSearch ?? {};
        if (edges?.length) {
          this.adventureEdges = this.adventureEdges.concat(edges);
          this.hasNextPage = pageInfo?.hasNextPage;
        }
        this.isLoading = false;
        this.isLoadingMore = false;

        // Focus on first result
        if (edges?.length) {
          const id = edges[0]?.node?.adventure?.id;
          const element = document.querySelector(`#adventure-${id} a`);
          if (element != null) (element as HTMLElement).focus();
        }

        this.setAdventureSearchCookies();
      } catch (error) {
        console.error('Search failed:', error);
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },
    adventureDetailsUrl(adventureLocation, adventureId): string | null {
      return adventures.getAdventureDetailsUrl(adventureLocation, adventureId);
    },
    firstLineText(sitterFirstName, sitterLastInitial): string | null {
      return adventures.getFirstLineText('base', null, null, sitterFirstName, sitterLastInitial);
    },
    secondLineText(ageRangeText, adventureCategoryName): string | null {
      return adventures.getSecondLineText(ageRangeText, adventureCategoryName);
    },
  },
  apollo: {
    adventureMetaData: {
      query: ADVENTURE_METADATA_QUERY,
    },
    allCategories: {
      query: ADVENTURE_METADATA_QUERY,
    },
  },
};
</script>
